import { Box, Button, Card, Container, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../../hooks/use-auth'
import { AppRoutes } from '../Routes'
import miLaunchpadLogo from '../../assets/milaunchpad.png'

interface LoginPageProps {
  readonly returnUrl?: string
}

// Development-only sign-in against the Test-env Account/ManualLogin endpoint (404 outside
// IsTest), so seeded personas can be exercised without the SSO redirect the Test env lacks.
const DEV_PERSONAS = [
  { email: 'p2p.midway@user.com', label: 'District User (mid-progress collection)' },
  { email: 'isd@admin.com', label: 'ISD Admin (dashboard + invitations)' },
  { email: 'rcn@admin.com', label: 'RCN Admin (roll-up dashboard)' },
  { email: 'start@admin.com', label: 'Data Manager (statewide)' },
  { email: 'p2p.invited@user.com', label: 'Invited user (auto-provision)' }
]

function DevLoginPanel ({ returnUrl }: LoginPageProps): JSX.Element {
  const auth = useAuth()
  const nav = useNavigate()
  const serverUrl = process.env.REACT_APP_BACKEND_URL ?? ''
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')

  const devLogin = async (loginEmail: string): Promise<void> => {
    setError('')
    try {
      // ManualLogin takes a bare JSON string body, which sendPost's object-only signature can't express.
      const response = await fetch(`${serverUrl}/Account/ManualLogin`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginEmail)
      })
      if (!response.ok) {
        setError(`ManualLogin failed (${response.status}). Is the backend running in the Test environment?`)
        return
      }
      await auth.setCurrentUser()
      nav(returnUrl ?? AppRoutes.HOME)
    } catch {
      setError('Could not reach the backend. Accept the cert at ' + serverUrl + ' and confirm it is running (Test env).')
    }
  }

  return (
    <Card sx={{ mb: '1.25em', border: '2px dashed var(--blue-500)' }} data-testid='devLoginPanel'>
      <Container sx={{ py: '1em' }}>
        <Typography variant='h4' component='h1'>Dev sign-in (Test env only)</Typography>
        <Typography variant='body2' sx={{ mb: '.75em' }}>
          Email-only login via ManualLogin — for exercising seeded personas without SSO.
        </Typography>
        <Typography variant='subtitle2' sx={{ mb: '.25em' }}>Seeded test personas</Typography>
        <Stack spacing={1} sx={{ mb: '1em' }}>
          {DEV_PERSONAS.map(p => (
            <Button
              key={p.email}
              variant='outlined'
              size='small'
              sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
              data-testid={`devLogin-${p.email}`}
              onClick={() => { void devLogin(p.email) }}
            >
              {p.email} — {p.label}
            </Button>
          ))}
        </Stack>
        <Stack direction='row' spacing={1}>
          <TextField
            size='small'
            placeholder='custom email'
            value={email}
            onChange={(e) => { setEmail(e.target.value) }}
            inputProps={{ 'data-testid': 'devLoginEmail' }}
          />
          <Button variant='contained' sx={{ textTransform: 'none' }} onClick={() => { void devLogin(email) }}>Sign in</Button>
        </Stack>
        {error !== '' && <Typography variant='body2' color='error' sx={{ mt: '.5em' }}>{error}</Typography>}
      </Container>
    </Card>
  )
}

export function LoginPage ({ returnUrl }: LoginPageProps): JSX.Element {
  const auth = useAuth()
  const isDev = process.env.NODE_ENV === 'development'

  return <Box>
    <Typography variant='h1'> Login </Typography>

    {isDev && <Box sx={{ mt: '1.25em', maxWidth: '520px' }}><DevLoginPanel returnUrl={returnUrl} /></Box>}

    <Box sx={{ display: 'flex', mt: '1.25em' }}>
      <Card>
        <Container>
          <Typography variant='h4' component='h1'>
            MiCredentials
          </Typography>

          <Typography variant='body1'>
            Use your MiCredentials login information to log in to START.
          </Typography>

          <Button
            variant='contained'
            color='primary'
            onClick={() => { auth.miCredsLogin(returnUrl) }}
            sx={{ mt: '1.25em', mb: '.625em', textTransform: 'none' }}
          >
            Login
          </Button>
        </Container>
      </Card>

      <Card sx={{ ml: '1.25em' }}>
        <Container>
          <Typography variant='h4' component='h1'>
            MiLaunchPad
          </Typography>

          <Typography variant='body1'>
            Use your local district login information to log in to START.
          </Typography>

          <Box>
            <img alt={'Powered by MiLaunchPad'} src={miLaunchpadLogo} />
          </Box>

          <Button
            variant='contained'
            color='primary'
            onClick={() => { auth.miLaunchPadLogin(returnUrl) }}
            sx={{ mt: '1.25em', mb: '.625em', textTransform: 'none' }}
          >
            Login
          </Button>
        </Container>
      </Card>
    </Box>
  </Box>
}
